import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthorizedUsersSkeleton } from './AuthorizedUsersSkeleton';
import { NS } from './i18n';
import './AccessVerificationSkeleton.css';

const POLICIES = [0, 1];
const HEAD_CELLS = [0, 1, 2];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`access-verification-skeleton__bone ${className}`} />
);

export interface AccessVerificationSkeletonProps {
  /** Adds the agent screens' gutter, for RouteSkeleton, which draws it straight in the app chrome. */
  standalone?: boolean;
}

/**
 * Loading shape of Access & Verification: breadcrumb, then the card with the
 * heading, the access policy and the users list. No `aria-busy`: on a live
 * region it holds the announcement back.
 */
export const AccessVerificationSkeleton: React.FC<AccessVerificationSkeletonProps> = ({
  standalone = false,
}) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className={`access-verification-skeleton${standalone ? ' access-verification-skeleton--standalone' : ''}`}
      role="status"
      data-testid="access-verification-skeleton"
    >
      <span className="access-verification-skeleton__sr-only">{text}</span>
      <div className="access-verification-skeleton__header" aria-hidden="true">
        <Bone className="access-verification-skeleton__crumb" />
      </div>
      <div className="access-verification-skeleton__card" aria-hidden="true">
        <div className="access-verification-skeleton__heading">
          <Bone className="access-verification-skeleton__title" />
          <Bone className="access-verification-skeleton__line access-verification-skeleton__line--long" />
        </div>

        <div className="access-verification-skeleton__policy-section">
          <Bone className="access-verification-skeleton__section-title" />
          <Bone className="access-verification-skeleton__line access-verification-skeleton__line--medium" />
          {POLICIES.map((policy) => (
            <div key={policy} className="access-verification-skeleton__policy">
              <Bone className="access-verification-skeleton__dot" />
              <div className="access-verification-skeleton__policy-text">
                <Bone className="access-verification-skeleton__line access-verification-skeleton__line--short" />
                <Bone className="access-verification-skeleton__line access-verification-skeleton__line--long" />
              </div>
            </div>
          ))}
        </div>

        <div className="access-verification-skeleton__users">
          <div className="access-verification-skeleton__users-head">
            <div className="access-verification-skeleton__users-title">
              <Bone className="access-verification-skeleton__section-title" />
              <Bone className="access-verification-skeleton__line access-verification-skeleton__line--medium" />
            </div>
            <Bone className="access-verification-skeleton__button" />
          </div>
          <div className="access-verification-skeleton__list">
            <div className="access-verification-skeleton__list-head">
              {HEAD_CELLS.map((cell) => (
                <Bone key={cell} className="access-verification-skeleton__head-cell" />
              ))}
            </div>
            <AuthorizedUsersSkeleton rows={4} announce={false} />
          </div>
        </div>
      </div>
    </div>
  );
};
